export interface AuthUser {
  mobileNumber: string;
  token: string;
  iat: number;
  exp: number;
}

export class AuthService {
  private static readonly TOKEN_KEY = 'mcc_jwt_token';
  private static readonly USER_KEY = 'mcc_user_data';

  /**
   * Decode JWT token to get payload
   */
  private static decodeToken(token: string): any | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Store JWT token and user data in localStorage
   */
  static setToken(token: string, userData: any): void {
    try {
      localStorage.setItem(this.TOKEN_KEY, token);
      
      // Decode token to get expiration and other JWT payload data
      const decodedToken = this.decodeToken(token);
      const userDataWithToken = {
        ...userData,
        ...decodedToken // This includes exp, iat, mobileNumber, token fields
      };
      
      localStorage.setItem(this.USER_KEY, JSON.stringify(userDataWithToken));
    } catch (error) {
      console.error('Error storing authentication data:', error);
    }
  }

  /**
   * Get JWT token from localStorage
   */
  static getToken(): string | null {
    try {
      return localStorage.getItem(this.TOKEN_KEY);
    } catch (error) {
      console.error('Error retrieving token:', error);
      return null;
    }
  }

  /**
   * Get user data from localStorage
   */
  static getUser(): any | null {
    try {
      const userData = localStorage.getItem(this.USER_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error retrieving user data:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    // Check if token is expired
    const user = this.getUser();
    if (!user || !user.exp) return false;

    const currentTime = Math.floor(Date.now() / 1000);
    return user.exp > currentTime;
  }

  /**
   * Clear authentication data
   */
  static logout(): void {
    try {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.USER_KEY);
    } catch (error) {
      console.error('Error clearing authentication data:', error);
    }
  }

  /**
   * Get authorization header for API requests
   */
  static getAuthHeader(): { Authorization: string } | null {
    const token = this.getToken();
    if (!token || !this.isAuthenticated()) {
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }

  /**
   * Check if token will expire soon (within 2 minutes)
   */
  static isTokenExpiringSoon(): boolean {
    const user = this.getUser();
    if (!user || !user.exp) return false;

    const currentTime = Math.floor(Date.now() / 1000);
    const twoMinutes = 2 * 60; // 2 minutes in seconds
    return (user.exp - currentTime) < twoMinutes;
  }

  /**
   * Get time until token expires in seconds
   */
  static getTimeUntilExpiry(): number {
    const user = this.getUser();
    if (!user || !user.exp) return 0;

    const currentTime = Math.floor(Date.now() / 1000);
    return Math.max(0, user.exp - currentTime);
  }

  /**
   * Get time until token expires in minutes
   */
  static getTimeUntilExpiryMinutes(): number {
    const seconds = this.getTimeUntilExpiry();
    return Math.ceil(seconds / 60);
  }

  /**
   * Check if token is expired and logout if needed
   */
  static checkAndHandleExpiry(): boolean {
    if (!this.isAuthenticated()) {
      this.logout();
      return true; // Token was expired
    }
    return false; // Token is still valid
  }

  /**
   * Force logout and redirect to My Crypto Card website
   */
  static forceLogout(): void {
    this.logout();
    // Redirect to My Crypto Card website
    window.location.href = 'https://www.mycryptocard.io/';
  }

  /**
   * Attempt silent refresh of JWT token
   */
  static async silentRefresh(): Promise<boolean> {
    try {
      const currentToken = this.getToken();
      if (!currentToken) {
        return false;
      }

      // Import ApiService dynamically to avoid circular dependency
      const { ApiService } = await import('./apiService');
      
      // Call backend refresh endpoint via ApiService
      const response = await ApiService.refreshToken();
      
      if (response.success && response.data?.token) {
        this.setToken(response.data.token, response.data.user);
        return true;
      }

      return false;
    } catch (error) {
      console.error('🔐 Silent refresh error:', error);
      return false;
    }
  }

  /**
   * Check if token can be refreshed (within 5 minutes of expiry)
   */
  static canRefreshToken(): boolean {
    const user = this.getUser();
    if (!user || !user.exp) return false;

    const currentTime = Math.floor(Date.now() / 1000);
    const fiveMinutes = 5 * 60; // 5 minutes in seconds
    const timeUntilExpiry = user.exp - currentTime;

    return timeUntilExpiry > 0 && timeUntilExpiry <= fiveMinutes;
  }
} 